import { useState } from "react";
import { trpc } from "../../../lib/api/trpc";

interface BulkOperationResult {
  successful: string[];
  failed: Array<{ userId: string; error: string }>;
}

/**
 * Hook for archiving users (single and bulk)
 */
export const useArchiveUser = () => {
  const [isArchiving, setIsArchiving] = useState(false);

  // @ts-ignore - Type checking for trpc.useContext
  const utils = trpc.useContext();

  // Single user archive mutation
  // @ts-ignore - Type checking for trpc.user property
  const archiveUserMutation = trpc.user.archiveUser.useMutation({
    onSuccess: () => {
      // @ts-ignore - Type checking for utils.user property
      utils.user.getUsers.invalidate();
      // @ts-ignore - Type checking for utils.user property
      utils.user.getUserById.invalidate();
    },
  });

  // Bulk archive mutation
  // @ts-ignore - Type checking for trpc.user property
  const bulkArchiveUsersMutation = trpc.user.bulkArchiveUsers.useMutation({
    onSuccess: () => {
      // @ts-ignore - Type checking for utils.user property
      utils.user.getUsers.invalidate();
    },
  });

  /**
   * Archive a single user
   */
  const archiveUser = async (
    userId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setIsArchiving(true);
      await archiveUserMutation.mutateAsync({ userId });
      // Success notification will be handled by the calling component
      return { success: true };
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to archive user";
      return { success: false, error: errorMessage };
    } finally {
      setIsArchiving(false);
    }
  };

  /**
   * Archive multiple users
   */
  const bulkArchiveUsers = async (
    userIds: string[],
  ): Promise<BulkOperationResult> => {
    try {
      setIsArchiving(true);
      const result = await bulkArchiveUsersMutation.mutateAsync({ userIds });

      // Success/error notifications will be handled by the calling component

      return result;
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to archive users";
      return {
        successful: [],
        failed: userIds.map((userId) => ({ userId, error: errorMessage })),
      };
    } finally {
      setIsArchiving(false);
    }
  };

  return {
    archiveUser,
    bulkArchiveUsers,
    isArchiving,
  };
};
